/* ================================
 * Session
 * ================================ */

/**
 * Response from the server when starting a FreedomPay HPC session. Mirrors the Razor model
 * (SessionKey / SessionKeys / HtmlIframe) that the legacy server-rendered flow used.
 */
export interface FreedomPaySession {
  /** The express checkout order id returned from init, passed back to complete. */
  orderId: string;
  /**
   * The iframe markup returned by FreedomPay to embed the hosted payment component. The session
   * key is baked into the iframe's src by the server, so the client never handles it directly.
   */
  htmlIframe: string;
}

/* ================================
 * postMessage protocol
 * ================================ */

/**
 * Message types posted by the FreedomPay HPC iframe to the parent window.
 * Mirrors the switch in the FreedomPay integration snippet.
 */
export enum FreedomPayMessageType {
  /** Iframe ready / no-op. */
  Ready = 1,
  /** Iframe reporting its content height for responsive resizing. */
  Resize = 2,
  /** Iframe delivering the captured payment SessionKeys payload. */
  SessionKeys = 3,
  /** Terminal / completion signal. */
  Complete = 4,
}

/** Shape of a message event's `data` posted by the HPC iframe. */
export interface FreedomPayMessage {
  type: FreedomPayMessageType;
  data: {
    height?: number;
    [key: string]: unknown;
  };
}

/** The result surfaced to the host once the card details have been captured. */
export interface FreedomPayResult {
  success: boolean;
  /** The stringified SessionKeys payload captured from the iframe. */
  sessionKeys?: string;
  error?: { message?: string };
}

/** Normalised error shape for FreedomPay SDK / HPC failures. */
export interface FreedomPayError {
  reason?: string;
  message?: string;
  details?: Array<{ location: string; message: string }>;
}
